import { useEffect, useRef } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { PublicationReleasePanel } from '@/features/publishing/publication-release-panel'
import { usePublicationRelease } from '@/features/publishing/use-publication-release'
import { publicationVerifiedDeploymentUrl } from '@/features/publishing/publication-release-types'

interface PublishModalProps {
  projectId: string
  slug: string
  currentVersion: number
  onClose: () => void
  onPublished: (url: string) => void
}

/**
 * Compatibility entry point for the toolbar and IDE header.
 *
 * "Publish" prepares and verifies an immutable candidate only. The component
 * intentionally has no direct call to the legacy project publish endpoint and
 * cannot perform an external deployment. Launch owns grant-bound deployment,
 * receipt verification, and independent readback.
 */
export function PublishModal({
  projectId,
  slug,
  currentVersion,
  onClose,
  onPublished,
}: PublishModalProps) {
  const publication = usePublicationRelease(projectId)
  const reportedUrl = useRef<string | null>(null)
  const verifiedUrl = publicationVerifiedDeploymentUrl(publication.release) ?? null

  useEffect(() => {
    if (!verifiedUrl || reportedUrl.current === verifiedUrl) return
    reportedUrl.current = verifiedUrl
    onPublished(verifiedUrl)
  }, [onPublished, verifiedUrl])

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && publication.pendingAction === null) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[51] max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-5xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-background shadow-xl focus:outline-none"
          data-testid="publication-candidate-dialog"
        >
          <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-border bg-background px-4 py-4 sm:px-5">
            <div className="min-w-0">
              <Dialog.Title className="text-sm font-semibold text-foreground">
                Prepare publication candidate
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs leading-5 text-muted-foreground">
                Saved revision v{currentVersion} for {slug}. This step creates and verifies a candidate; it does not deploy externally.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={publication.pendingAction !== null}
                aria-label="Close publication candidate"
                className="inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </Dialog.Close>
          </header>

          <div className="p-4 sm:p-5">
            <PublicationReleasePanel
              mode="candidate"
              controller={publication}
              projectId={projectId}
              slug={slug}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
